'use client';

import { useSession } from 'next-auth/react';
import FavoriteButton from '@/components/FavoriteButton';
import StockAvailability from '@/components/StockAvailability';
import { ROUTES } from '@/constants/application-urls';
import { useEllipsisStyles } from '@/hooks/useEllipsisStyles';
import ProductListingDictionary from '@/types/dictionaries/ProductListing';
import StockAvailabilityDictionary from '@/types/dictionaries/StockAvailability';
import { ProductSearchResult } from '@/types/gearApi/Product';
import { WishlistResponse } from '@/types/gearApi/Wishlist';
import { getConfiguredOrDefaultStockAvailability } from '@/utils/helpers/stock';
import { useStorefrontSettingsStore } from '@/utils/providers/StorefrontSettingsProvider';
import NoPhotographyOutlinedIcon from '@mui/icons-material/NoPhotographyOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import LocalizedLink from '@/components/LocalizedLink';
import ProductCardActionButton from '@/features/FeatureRefinedProductList/components/ProductCardActionButton';

export interface ProductCardProps {
	product: ProductSearchResult;
	priceLabel?: string;
	wishlists?: WishlistResponse;
	dict: ProductListingDictionary;
	stockAvailabilityDict: StockAvailabilityDictionary;
	sharedCartId: string | null;
	isBusinessToBusinessShop: boolean;
	requestQuoteUrl: string | null;
	contactUsUrl: string;
}

export default function ProductCard({
	product,
	priceLabel,
	wishlists,
	dict,
	stockAvailabilityDict,
	sharedCartId,
	isBusinessToBusinessShop,
	requestQuoteUrl,
	contactUsUrl,
}: Readonly<ProductCardProps>) {
	const { image, name, sku, slug, cardDescription, hasMultipleVariants, stockStatus, isQuoteOnly } = product;

	const { data: session } = useSession();
	const ellipsisStyles = useEllipsisStyles();
	const showInStockByDefault = useStorefrontSettingsStore((s) => s.showInStockByDefault);
	const stockAvailability = !isBusinessToBusinessShop
		? getConfiguredOrDefaultStockAvailability({
				stockStatus,
				stockAvailabilityDict,
				showInStockByDefault,
			})
		: null;
	const productDetailsUrl = `${ROUTES.PRODUCTS}/${slug}`;

	const showPriceLabel = !!priceLabel && !isQuoteOnly;

	return (
		<Card
			variant="outlined"
			sx={{
				height: '100%',
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
			}}
			data-testid="product-card"
		>
			{session && (
				<Box sx={{ ml: 'auto', pr: '15px', pt: '15px', height: '45px' }}>
					{!hasMultipleVariants && (
						<FavoriteButton
							initialState={!!wishlists?.lineItems.find((item) => item.sku === sku)}
							sku={sku}
							data-testid="product-card-favorite-button"
						/>
					)}
				</Box>
			)}

			<CardActionArea
				sx={{
					pb: '16px',
					position: 'static',
					textDecoration: 'none',
					color: 'black.main',
					fontFamily: 'inherit',
					height: '100%',
				}}
			>
				{/**
				 * Turn off NextJS Link default prefetch behavior: https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#2-prefetching
				 * this happens only after deployed on page scroll, not locally, which causes rate limits on Cloudflare.
				 * */}
				<LocalizedLink
					href={productDetailsUrl}
					prefetch={false}
					style={{ textDecoration: 'inherit', color: 'inherit', height: '100%', display: 'block' }}
					data-testid="product-card-cta"
				>
					{image ? (
						<CardMedia component="img" height="212" image={image.url} alt={image.label} sx={{ objectFit: 'contain' }} />
					) : (
						<Box
							sx={{
								width: '100%',
								height: '212px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<NoPhotographyOutlinedIcon sx={{ color: 'grey.medium' }} />
						</Box>
					)}

					<CardContent sx={{ pb: 0 }}>
						<Typography variant="h5" gutterBottom data-testid="product-card-name">
							{name}
						</Typography>
						<Typography
							variant="productInfo"
							component="div"
							gutterBottom
							color="grey.dark"
							mb={0}
							sx={{
								...ellipsisStyles,
								wordBreak: 'break-word',
							}}
							data-testid="product-card-product-sku"
						>
							{cardDescription}
						</Typography>
						<Box
							sx={{
								display: 'inline-flex',
								justifyContent: showPriceLabel ? 'space-between' : 'flex-end',
								width: '100%',
								pt: '16px',
							}}
						>
							{showPriceLabel && (
								<Typography variant="priceTiles" component="div" data-testid="product-card-price-label">
									{priceLabel}
								</Typography>
							)}
							{!hasMultipleVariants && !isQuoteOnly && stockAvailability && (
								<StockAvailability availability={stockAvailability} />
							)}
						</Box>
					</CardContent>
				</LocalizedLink>
			</CardActionArea>

			<CardActions sx={{ p: '16px', pt: 0 }}>
				<ProductCardActionButton
					priceLabel={priceLabel}
					product={product}
					productDetailsUrl={productDetailsUrl}
					requestQuoteUrl={requestQuoteUrl}
					contactUsUrl={contactUsUrl}
					sharedCartId={sharedCartId}
					dict={dict}
				/>
			</CardActions>
		</Card>
	);
}
